<template>
  <div class="funding-terms">
    <p>
      The Crowdfunding Platform (“Platform”) is operated by Custom Content
      Technology Ltd (“Found," "CCT," "us," "we," or "our”). The agreement to
      make a contribution (“Contribution”) in support of a creator’s (“Creator”)
      crowdfunding purpose (“Crowdfunding Purpose”) is between the Creator and
      the person making a Contribution ("Contributor"). These Terms and
      Conditions apply to all Contributions made on the Platform. Use of the
      Platform is also subject to Found’s General website terms found here:
      <a href="/terms">Terms of use | Found</a>. To the extent these Terms and
      Conditions conflict with Found’s General website terms or any separate
      Creator terms in regard to Contributions made on the Platform, these Terms
      and Conditions shall control. Found offers this Platform conditioned upon
      your acceptance of all the terms, conditions, policies, and notices stated
      herein. YOUR CONTINUED USE OF THE PLATFORM CONSTITUTES YOUR AGREEMENT TO
      THESE TERMS AND CONDITIONS. If at any time you do not agree to these Terms
      and Conditions, please do not use this Platform.
    </p>

    <p>
      <b>
        PLEASE READ THESE TERMS AND CONDITIONS CAREFULLY BEFORE MAKING A
        CONTRIBUTION. THE TERMS APPLY TO ANY PERSON THAT MAKES A CONTRIBUTION.
        BY MAKING A CONTRIBUTION, YOU CONFIRM THAT YOU HAVE READ AND UNDERSTOOD
        THE TERMS AND CONDITIONS AND AGREE TO BE BOUND BY THEM.
      </b>
    </p>

    <p>
      <b>
        NOTICE OF ARBITRATION: THESE TERMS AND CONDITIONS REQUIRE YOU TO SUBMIT
        MOST DISPUTES TO MANDATORY ARBITRATION, WHICH MEANS THAT YOU AGREE TO
        SUBMIT ANY DISPUTE RELATED TO THE PLATFORM, THESE TERMS AND CONDITIONS,
        OR OUR PRIVACY POLICIES TO BINDING INDIVIDUAL ARBITRATION RATHER THAN
        PROCEEDING IN COURT, EXCEPT FOR SMALL CLAIMS COURT. MORE INFORMATION
        ABOUT ARBITRATION IS SET FORTH BELOW.
      </b>
    </p>

    <p>
      <b>
        YOU MAY NOT ORDER OR OBTAIN PRODUCTS OR SERVICES FROM THIS PLATFORM IF
        YOU (A) DO NOT AGREE TO THESE TERMS AND CONDITIONS, (B) ARE NOT AT LEAST
        18 YEARS OF AGE, OR (C) ARE PROHIBITED FROM ACCESSING OR USING THIS
        PLATFORM OR ANY OF THIS PLATFORM’S CONTENTS, PRODUCTS OR SERVICES BY
        APPLICABLE LAW.
      </b>
    </p>

    <p>
      <b>
        You shall not use the PLATFORM for any illegal purposes, and you will
        use it in compliance with all applicable laws, rules, and regulations.
        You shall not use the PLATFORM in a way that may cause the PLATFORM to
        be interrupted, damaged, rendered less efficient or such that the
        effectiveness or functionality of the PLATFORM is in any way impaired.
        You agree not to attempt any unauthorized access to any part or
        component of the PLATFORM.
      </b>
    </p>

    <HideableParagraph title="The Platform and CCT’s Role" text=" ">
      <div class="legal-text">
        <p>
          CCT hosts Creator Pages on the Platform to collect contributions for
          the Creator’s applicable Crowdfunding Purpose, namely the production
          and/or publication of the Creator’s book ("Book"). The applicable Book
          may be hard copy and/or digital, as determined by the applicable
          Creator. CCT is not a bank, payment processor, broker, charity or
          financial advisor. CCT does not solicit donations, the existence of
          the Creator Page is not a solicitation of donations, and we do not
          engage in solicitation activities for ourselves or others.
        </p>
        <p>
          The Creator Pages are not, nor are they intended to be, loan-based
          crowdfunding or investment- based crowdfunding.
        </p>
      </div>
    </HideableParagraph>

    <HideableParagraph title="The Contribution Process" text=" ">
      <div class="legal-text">
        <h6><b>Eligibility Requirements</b></h6>
        <p>
          Contributors must be 18 years old and at least the age of majority in
          their applicable jurisdiction in order to make a Contribution.
          Additional eligibility requirements may apply. See the applicable
          Creator Page for details. By making a Contribution, you represent that
          you are in compliance with all applicable laws and regulations in your
          applicable jurisdiction.
        </p>

        <h6><b>Making a Contribution</b></h6>
        <p>
          You may make a Contribution by following the on-screen prompts on the
          applicable Creator Page. When making a Contribution, you must provide
          information about yourself (including your payment information) either
          to us or our Payment Processors that is accurate, complete and up to
          date in all respects.
        </p>
        <p>
          All Contributions must be made using one of our approved payment
          methods, as stated on the Platform.
        </p>
        <p>
          By making a Contribution, you represent and warrant that you are
          legally authorised to make the Contribution using your selected
          payment method. If for any reason it transpires that you are (or were)
          not authorised to use your payment method or the information you have
          provided is inaccurate, false, misleading or fraudulent, we reserve
          the right to cancel or reverse the Contribution and to report you to
          the appropriate authorities.
        </p>

        <h6><b>Minimum Contributions</b></h6>
        <p>
          There may be a minimum Contribution amount. If there is a minimum
          Contribution amount, it will be posted on the applicable Creator Page
        </p>

        <h6><b>Refunds</b></h6>
        <p>Except as provided herein, Contributions are non-refundable.</p>

        <h6><b>Errors</b></h6>
        <p>
          If you believe that an error has occurred with your Contribution,
          please contact us so we can attempt to resolve the issue. You can
          email us at <a href="mailto:hello@found.us">hello@found.us</a>
        </p>

        <h6><b>Payment Processors </b></h6>
        <p>
          CCT does not handle the actual processing of payments. Instead, we use
          third-party payment processors (“Payment Processors”) to manage and
          process all Contributions. In order to make a Contribution, you may be
          required to register with and/or provide information to the Payment
          Processor. All Contributions are subject to the applicable Payment
          Processors’ privacy policy and terms and conditions. By making a
          Contribution, you confirm that you have read and understood the
          Payment Processors’ privacy policy and applicable terms and
          conditions. You also agree to the processing, use, transfer or
          disclosure of data by the Payment Processors set forth in these Terms
          and Conditions and any applicable Payment Processors’ terms and
          conditions.
        </p>

        <p>
          Our current Payment Processors include: Stripe Technology Company
          Limited – please click <a href="/terms">here</a> for Privacy Policy
          and Terms and Conditions.
        </p>

        <h6><b>Crowdfunding Period and Target</b></h6>
        <p>
          The Crowdfunding Period and Target will be displayed on the applicable
          Creator Page. The Crowdfunding Period and/or Target is subject to
          change, as determined by CCT and/or the applicable Creator. All such
          changes will be posted on the applicable Creator Page. Further,
          Creator Pages may be suspended or terminated at any time. Contributors
          are responsible for regularly checking the applicable Creator Page for
          all updates.
        </p>

        <h6><b>Contribution Refunds</b></h6>
        <p>
          All Contributions shall be refunded in the following circumstances:
        </p>

        <ul>
          <li>
            The Creator does not reach the Crowdfunding Target by the end of the
            Crowdfunding Period;
          </li>
          <li>
            CCT cancels or terminates a Creator Page for regulatory, legal or
            operational purposes; and/or
          </li>
          <li>
            Either the Creator or CCT decides, at their sole discretion, not to
            publish the Book.
          </li>
        </ul>
        <p>
          When issuing a refund to you, we will be entitled to deduct or retain
          any third-party fees, including banking fees, before remitting the
          balance to you. The refund will be paid to the same account used to
          make the Contribution.
        </p>
        <p>
          In the event you are issued a refund under these Terms and Conditions,
          you will no longer receive any Book(s) associated with your
          Contribution.
        </p>

        <h6><b>Contributor Acknowledgement and Risk</b></h6>
        <p>
          All Contributions are made at the Contributor’s own risk. By making a
          Contribution, the Contributor acknowledges and accepts that CCT cannot
          guarantee the success, completion or fulfilment of any Crowdfunding
          Target.
        </p>
        <p>
          The Contributor understands that a Creator may not reach its
          Crowdfunding Target or may otherwise be delayed or cancelled.
        </p>
        <p>
          Even where a Creator achieves its Crowdfunding Target, the Contributor
          acknowledges that the relevant Book may still not be published.
        </p>
        <p>
          To the fullest extent permitted by law, CCT shall not be responsible
          for any loss or damages (whether direct or indirect) arising from
          delays, cancellations, or failure of a Creator to proceed to
          completion or for a Book to be published.
        </p>

        <h6><b>Fraud</b></h6>
        <p>
          We take any reports of fraud or misuse of funds very seriously. If you
          have reason to believe that a Creator is promoting their Creator Page
          in a way that is not reflective of the Crowdfunding Purpose, please
          contact:
          <a href="mailto:louise.stephenson@found.us"
            >louise.stephenson@found.us</a
          >
        </p>
      </div>
    </HideableParagraph>

    <HideableParagraph title="Supply of Books" text=" ">
      <div class="legal-text">
        <p>
          In exchange for a Contribution, Creators may offer Contributors the
          opportunity to preorder the applicable Book, or to receive a copy of
          the applicable Book. Such offers are strictly conditioned on the
          Crowdfunding Target being met, the book being completed and published,
          and CCT and the Creator being permitted to lawfully distribute the
          book in your applicable jurisdiction.
        </p>
        <p>
          In the event you are eligible to receive a Book, CCT will notify you
          via email. Distribution, receipt, and/or purchase of any Book is
          subject to CCT’s standard terms of supply set out here:
          <a href="https://found.us/terms">https://found.us/terms</a> (as may be
          updated by us from time to time).
        </p>
        <p>
          You are responsible for providing an accurate email and/or mailing
          address when making a Contribution. You are responsible for providing
          accurate delivery details and for any customs duties, import taxes, or
          local delivery restrictions that may apply.
        </p>

        <h6><b>Returns and Cancellations</b></h6>
        <p>
          <u
            >Your cancellation rights in relation to physical copies of a
            book:</u
          >
        </p>
        <p>
          You have a legal right to cancel the supply of a physical Book and
          receive a refund, including the delivery costs.
        </p>
        <p>
          To exercise your right to cancel, you must notify us within 14 days of
          delivery by contacting us at
          <a href="mailto:hello@found.us">hello@found.us</a>
        </p>
        <p>
          Subject to the provisions of this clause, if you exercise your
          cancellation right, you are entitled to a refund equal to the
          difference between your Contribution and the Maximum Standard
          Donation, plus delivery costs if applicable.
        </p>
        <p>
          <i
            >Example: the Donation for a Creator Fundraiser is £30. The Maximum
            Standard Donation is £20. If you exercise your right to change your
            mind, you are entitled to receive a refund of £10.
          </i>
        </p>
        <p>
          This cancellation right does not apply to a Book that has been clearly
          personalised (this includes where the Book is inscribed).
        </p>
        <p>
          You must return the Book in order to be eligible for a refund. Returns
          are at your own cost, and you must send the Book back to us using an
          established delivery service. For help with returns, please contact us
          at <a href="mailto:hello@found.us">hello@found.us</a>
        </p>
        <p>
          If we don’t receive the Book at all or within 14 days from your
          notification under this section, and you are unable to provide proof
          of postage, we will not issue a refund.
        </p>
        <p>
          If the Book is returned in a condition which would not be acceptable
          in-store, we reserve the right to reduce your refund to reflect the
          reduced value.
        </p>
        <p>
          We refund standard delivery costs only – additional charges like
          express delivery or a selected time slot are not refundable.
        </p>
        <p>
          If you have not received email confirmation that your return has been
          processed within 14 days of returning the Book, or for any other help
          with returns, contact us at
          <a href="mailto:hello@found.us">hello@found.us</a>
        </p>
        <p><u>Your cancellation rights in relation to digital Books:</u></p>
        <p>
          When you make a Contribution and are eligible to receive a digital
          Book, you acknowledge that you will immediately lose your right to
          cancel your order as soon as we have supplied you a digital copy of
          the Book.
        </p>

        <h6><b>Faulty or Incorrect Book</b></h6>
        <p>
          If you think there is something wrong with your Book, you must contact
          us at <a href="mailto:hello@found.us">hello@found.us</a>. We honour
          our legal duty to provide you with a Book that was described to you on
          our Platform and that meets all the applicable legal requirements.
        </p>
      </div>
    </HideableParagraph>

    <HideableParagraph title="Errors and Inaccuracies" text=" ">
      <div class="legal-text">
        <p>
          We strive to provide complete, accurate, up-to-date information on the
          Platform. Unfortunately, despite those efforts, human or technological
          errors may occur. The Platform may contain typographical mistakes,
          inaccuracies, or omissions, some of which may relate to pricing and
          availability of products, and some information may not be complete or
          current. We reserve the right to correct any errors, inaccuracies or
          omissions, including after an order has been submitted, and to change
          or update information at any time without prior notice.
        </p>
        <p>
          You acknowledge that the particular technical specifications and
          settings of your computer and its display could affect the accuracy of
          its display.
        </p>
        <p>
          The content on an applicable Creator Page is provided for general
          information only. It is not intended to amount to advice on which you
          should rely. You must obtain professional or specialist advice before
          taking, or refraining from, any action on the basis of the content on
          a Creator Page.
        </p>
        <p>
          We do not guarantee that the Platform, and Creator Page, or any
          content on it, will always be available or be uninterrupted. We may
          suspend or withdraw or restrict the availability of all or any part of
          the Platform or a Creator Page for business and operational reasons.
          We will try to give you reasonable notice of any suspension or
          withdrawal.
        </p>
      </div>
    </HideableParagraph>

    <HideableParagraph title="External Websites and Resources" text=" ">
      <div class="legal-text">
        <p>
          We are not responsible for the availability of any websites owned or
          controlled by third-parties. We do not endorse and are not responsible
          or liable, directly or indirectly, for the privacy practices or the
          content (including misrepresentative or defamatory content) of any
          third party websites, including (without limitation) any advertising,
          products or other materials or services on or available from such
          websites or resources, nor for any damage, loss or offense caused or
          alleged to be caused by, or in connection with, the use of or reliance
          on any such content, goods or services available on such third-party
          external sites or resources.
        </p>
      </div>
    </HideableParagraph>

    <HideableParagraph title="Intellectual Property Ownership and Use" text=" ">
      <div class="legal-text">
        <p>
          You acknowledge and agree that all of our trademarks, logos,
          copyrights and any and all other intellectual property rights in all
          material or content contained within this Platform shall remain at all
          times vested in us or, in the cases where we are using such material
          or content under authority from a third party, in the owner of such
          material or content.
        </p>
        <p>
          We grant you the limited right to access and make use of the Platform
          as a Contributor. However, you shall not: (a) reproduce, duplicate,
          copy, sell or otherwise exploit the Platform or any image, page
          layout, page design, trade dress, trademark, logo or other content
          (“Platform Content”) for any commercial purpose; (b) use a robot,
          spider or data mining or extraction tool or process to monitor,
          extract or copy Platform Content; (c) use any meta tags, search terms,
          key terms, or the like that contain the Platform’s name or our
          trademarks; (d) engage in any activity that interferes with the
          Platform or another user’s ability to use the Platform; (e) modify,
          create derivative works from, reverse engineer, decompile or
          disassemble any technology used to provide the Platform and the goods
          or services offered on the Platform; or (f) assist or encourage any
          third party in engaging in any activity prohibited by these Terms and
          Conditions.
        </p>
        <p>
          You shall not use, copy, distribute, or exploit any of the Platform
          Content in any manner without our prior written permission.
        </p>
        <p>
          All Platform Content and all materials and content contained within
          the Platform, including but not limited to the text, graphics, logos,
          icons, images, audio clips, video clips, articles, posts and data
          appearing on the Platform, are owned by us, or used by us under
          authorization, and are protected by U.S. and foreign trademark and
          copyright laws. No portion of the materials or content on these pages
          may be reprinted or republished in any form without our express
          written permission.
        </p>
      </div>
    </HideableParagraph>

    <HideableParagraph
      title="Changes to Platform or These Terms and Conditions"
      text=" "
    >
      <div class="legal-text">
        <p>
          Other than as may be required by law, we reserve the right to modify
          or withdraw, temporarily or permanently, the Platform (or any part of)
          with or without notice to you, and you confirm that we shall not be
          liable to you or any third party for any modification to withdraw or
          withdrawal of the Platform or any portion of it.
        </p>
        <p>
          We may alter these Terms and Conditions from time to time, and your
          use of the Platform (or any part of the Platform) following such
          change shall be deemed to be your acceptance of such change. It is
          your responsibility to check regularly to determine whether the Terms
          and Conditions have been changed. If you do not agree to any change to
          the Terms and Conditions, then you must immediately stop using the
          Platform.
        </p>
        <p>
          The Platform is subject to constant change. You will not be eligible
          for any compensation because you cannot use any part of the Platform
          or because of a failure, suspension or withdrawal of all or part of
          the Platform.
        </p>
      </div>
    </HideableParagraph>

    <HideableParagraph title="Disclaimers" text=" ">
      <div class="legal-text">
        <p>
          WE MAKE NO WARRANTIES, WHETHER EXPRESS OR IMPLIED IN RELATION TO THE
          ACCURACY OF ANY INFORMATION ON THE PLATFORM. THE PLATFORM IS PROVIDED
          ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT ANY REPRESENTATION. WE
          MAKE NO WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, IN
          RELATION TO THE PLATFORM, INCLUDING BUT NOT LIMITED TO, IMPLIED
          WARRANTIES OF SATISFACTORY QUALITY, FITNESS FOR A PARTICULAR PURPOSE,
          NON-INFRINGEMENT, COMPATIBILITY, SECURITY, ACCURACY, CONDITION OR
          COMPLETENESS, OR ANY IMPLIED WARRANTY ARISING FROM COURSE OF DEALING
          OR USAGE OR TRADE.
        </p>
        <p>
          WE MAKE NO WARRANTY THAT THE PLATFORM WILL MEET YOUR REQUIREMENTS OR
          WILL BE UNINTERRUPTED, TIMELY, SECURE OR ERROR-FREE, THAT DEFECTS WILL
          BE CORRECTED, OR THAT THE PLATFORM OR THE SERVER THAT MAKES IT
          AVAILABLE ARE FREE OF VIRUSES OR BUGS OR ARE FULLY FUNCTIONAL,
          ACCURATE, OR RELIABLE.
        </p>
        <p>
          AS SET FORTH IN THE PRIVACY POLICY, YOU ACKNOWLEDGE THAT WE CANNOT
          GUARANTEE, AND THEREFORE SHALL NOT BE IN ANY WAY RESPONSIBLE FOR, THE
          SECURITY OR PRIVACY OF THE PLATFORM AND ANY INFORMATION PROVIDED TO OR
          TAKEN FROM THE PLATFORM BY YOU.
        </p>
      </div>
    </HideableParagraph>

    <HideableParagraph title="Limitations of Liability" text=" ">
      <div class="legal-text">
        <p>
          WE WILL NOT BE LIABLE FOR ANY ECONOMIC LOSSES (INCLUDING WITHOUT
          LIMITATION LOSS OF REVENUES, PROFITS, CONTRACTS, BUSINESS OR
          ANTICIPATED SAVINGS) OR ANY LOSS OF GOODWILL OR REPUTATION, OR ANY
          LOSS OR CORRUPTION OF DATA, OR ANY SPECIAL OR INDIRECT OR
          CONSEQUENTIAL LOSSES ARISING OUT OF YOUR USE OF THE PLATFORM; IN ANY
          CASE WHETHER OR NOT SUCH LOSSES WERE WITHIN THE CONTEMPLATION OF US AT
          THE DATE ON WHICH THE EVENT GIVING RISE TO THE LOSS OCCURRED.
        </p>
        <p>
          SOME JURISDICTIONS DO NOT ALLOW THE LIMITATIONS OR EXCLUSION OF
          LIABILITY FOR INCIDENTAL OR CONSEQUENTIAL DAMAGES, SO THE ABOVE MAY
          NOT APPLY TO YOU.
        </p>
      </div>
    </HideableParagraph>

    <HideableParagraph title="Indemnification" text=" ">
      <div class="legal-text">
        <p>
          YOU AGREE TO BE FULLY RESPONSIBLE FOR (AND FULLY INDEMNIFY US AGAINST)
          ALL CLAIMS, LIABILITY, DAMAGES, LOSSES, COSTS AND EXPENSES, INCLUDING
          LEGAL FEES, SUFFERED BY US AND ARISING OUT OF ANY BREACH OF YOUR
          REPRESENTATIONS AND WARRANTIES OR THESE TERMS AND CONDITIONS BY YOU OR
          ANY OTHER LIABILITIES ARISING OUT OF YOUR USE OF THE PLATFORM, OR THE
          USE BY ANY OTHER PERSON ACCESSING THE PLATFORM USING YOUR COMPUTER OR
          INTERNET ACCESS ACCOUNT.
        </p>
      </div>
    </HideableParagraph>

    <HideableParagraph
      title="Binding Arbitration Agreement and Class-Action Waiver"
      text=" "
    >
      <div class="legal-text">
        <p>
          <strong>Arbitration Agreement.</strong> Except as otherwise stated
          below, any claim or dispute between you and us (or any of our
          subsidiaries or affiliates) arising out of relating in any way to the
          Platform or our products or services shall be resolved through final,
          binding arbitration. This arbitration obligation applies regardless of
          whether the claim or dispute involves a breach of contract, tort,
          fraud, misrepresentation, product liability, negligence, violation of
          a statute, or any other legal theory. Both you and we specifically
          acknowledge and agree to waive your right to bring a lawsuit based on
          such claim(s) or dispute(s) and your right to have such lawsuit
          resolved by a judge or a jury.
        </p>
        <p>
          This Arbitration Agreement shall be governed by the Federal
          Arbitration Act, (9 U.S.C. § 1 et seq.). Any arbitration will be
          commenced and administered by JAMS under the rules of JAMS, including
          JAMS Consumer Arbitration Minimum Standards
          <a href="https://www.jamsadr.com/consumer-minimum-standards/"
            >https://www.jamsadr.com/consumer-minimum-standards/</a
          >. Provided JAMS’ criteria are met by the nature of the dispute, the
          arbitration shall be conducted under JAMS Streamlined Arbitration
          Rules & Procedures
          <a href="https://www.jamsadr.com/rules-streamlined-arbitration/"
            >https://www.jamsadr.com/rules-streamlined-arbitration/</a
          >. Each party will pay the fees for his/her or its own attorneys,
          subject to any remedies to which that party may later be entitled
          under applicable law. However, if you are unable to pay JAMS’ costs,
          we will pay all arbitration fees and expenses. If JAMS is unavailable,
          unwilling, or otherwise unable to administer an arbitration in
          accordance with these rules, then another administrator that will do
          so will be selected by agreement of the parties.
        </p>

        <p>
          The arbitrator has the same authority to award relief on an individual
          basis that a judge in a court of law would have. The award of the
          arbitrator is final and binding upon both you and us. Additionally,
          the arbitrator, and not any federal, state, or local court or agency,
          shall have the exclusive authority to resolve any dispute relating to
          the interpretation, applicability, enforceability, or formation of
          this Arbitration Agreement, except that this sentence shall not apply
          to the Class Action Waiver provisions described below. The arbitrator
          will render a decision in writing. A court of competent jurisdiction
          shall have the authority to enter judgment upon the arbitrator's
          decision/award.
        </p>

        <p>
          This Arbitration Agreement will survive the termination of your
          relationship with us.
        </p>

        <p>
          <strong>Excluded Disputes.</strong> You and we agree that the
          following Disputes are excluded from this Arbitration Agreement: (1)
          any dispute, claim, or controversy arising out of or relating to an
          alleged violation of a party's intellectual property rights, including
          but not limited to claims of patent, copyright, trademark, or trade
          secret infringement. This category of claims and disputes shall be
          resolved through litigation in a court of competent jurisdiction,
          rather than through arbitration; (2) individual claims brought in
          small claims court; (3) any claim that an applicable federal statute
          expressly states cannot be arbitrated; and (4) any claim for
          injunctive relief. Any challenges or issues as to the enforceability
          or applicability of the arbitration and class-action waiver provisions
          shall be decided in the first instance by the arbitrator.
        </p>

        <p>
          <strong>Class Action Waiver.</strong> To fullest extent permitted by
          applicable law, you and we agree to bring any claim or dispute,
          whether in arbitration, or court as permitted by these terms, ON AN
          INDIVIDUAL BASIS ONLY, AND NOT AS A CLASS ACTION OR COLLECTIVE ACTION.
          There shall be no right or authority for any claim or dispute to be
          brought, heard or arbitrated as a class or collective action ("Class
          Action Waiver"). The arbitration will decide the rights and
          liabilities, if any, of you and us. The arbitration proceeding will
          not be consolidated with any other matters or joined with any other
          cases or parties. The arbitrator may award any remedy to which a party
          is entitled under applicable law, but remedies shall be limited to
          those that would be available to a party in their individual capacity.
          Regardless of anything else in this Arbitration Agreement and/or the
          applicable JAMS rules, the interpretation, applicability,
          enforceability or formation of the Class Action Waiver may only be
          determined by a court and not an arbitrator. This Class-Action Waiver
          will survive the termination of your relationship with us.
        </p>

        <p>
          <strong>One-Year Time Limit to Raise Disputes or Claims.</strong> You
          agree that any dispute, claim or lawsuit, regardless of form, that may
          arise out of or related to these Terms or your use of the Platform,
          must be filed within ONE (1) YEAR of the action, omission, event or
          occurrence giving rise to the dispute, claim(s) or lawsuit. After the
          expiration of the one-year period, such dispute, claim(s) or lawsuit
          will be time-barred and prohibited, without regard to any longer
          period of time which may be provided by any period of limitation or
          pursuant to law or statute.
        </p>

        <p>
          <strong>Thirty-Day Right to Opt Out.</strong> You have the right to
          opt out of the arbitration requirement by sending written notice of
          your decision to opt out to the following e-mail address:
          <a href="mailto:hello@found.us">hello@found.us</a> within thirty days
          after first becoming subject to these terms. Your notice must include
          your name and address, the e-mail address you used to set up your
          online account (if you have one), and an unequivocal statement that
          you want to opt out of the arbitration requirement. In the event you
          opt out, all other parts of this Agreement will continue to apply.
          Opting out of this arbitration agreement has no effect on any other
          obligations to arbitrate that you may currently have, or may enter in
          the future, with us. This opt-out right does not apply to the
          class-action waiver provision.
        </p>

        <p>
          <strong>Waiver of Jury Trial.</strong> BOTH YOU AND WE HEREBY WAIVE
          ANY CONSTITUTIONAL AND STATUTORY RIGHTS TO SUE IN COURT AND HAVE A
          TRIAL IN FRONT OF A JUDGE OR A JURY, except as provided herein. We are
          instead mutually electing that all disputes, claims, or requests for
          relief shall be resolved by arbitration under this Arbitration
          Agreement, except as specified herein. An arbitrator can award on an
          individual basis the same damages and relief as a court and must
          follow this Agreement as a court would. However, there is no judge or
          jury in arbitration, and court review of an arbitration award is
          subject to very limited review.
        </p>

        <p>
          <strong>Location.</strong> You may choose to have the arbitration
          conducted by video conference technology such as Zoom, or based on
          written submissions. Otherwise, the location of the arbitration
          proceeding shall take place in the city or state where you reside,
          unless each party agrees otherwise, or at another mutually agreed upon
          location.
        </p>

        <p>
          <strong>Severability.</strong> If for any reason, any portion of these
          Terms, including this arbitration section, is found to be illegal,
          void or unenforceable, that portion will be severed, and it shall not
          affect the validity and enforceability of the remainder of these Terms
          which shall continue in full force and effect.
        </p>
      </div>
    </HideableParagraph>

    <HideableParagraph title="Governing Law" text=" ">
      <div class="legal-text">
        <p>
          To the fullest extent permitted by applicable laws, these Terms and
          Conditions and any dispute that might arise between you and us will be
          governed by the laws of the State of New York and applicable federal
          laws of the United States of America, consistent with the Federal
          Arbitration Act (9 U.S.C. § 1 et seq.)., without regard to any
          principle of conflict-of-laws. You further agree that any dispute that
          is not subject to arbitration or eligible for small claims actions
          shall be heard, venued, and decided exclusively by a court of
          competent jurisdiction located in New York County in the State of New
          York, or in the United States District Court for the Southern District
          of New York and that you waive all objections to such jurisdiction and
          venue.
        </p>
      </div>
    </HideableParagraph>

    <HideableParagraph
      title="Investigations of Violations of These Terms"
      text=" "
    >
      <div class="legal-text">
        <p>
          We may investigate any reported violation of these Terms and
          Conditions and take any action that we deem appropriate. Such action
          may include, but is not limited to, issuing warnings, removing posted
          content and/or reporting any activity that we suspect violates any law
          or regulation to appropriate law enforcement officials, regulators, or
          other third parties.
        </p>
      </div>
    </HideableParagraph>

    <HideableParagraph title="Notice for California Users" text=" ">
      <div class="legal-text">
        <p>
          If our Platform, or any of our services is deemed an “electronic
          commercial service” under California Civil Code Section 1789.3,
          residents of California are entitled to know that they may file
          grievances and complaints with the California Department of Consumer
          Affairs. Information on how to file a complaint can be found here –
          <a href="https://www.dca.ca.gov/consumers/complaints"
            >https://www.dca.ca.gov/consumers/complaints</a
          >.
        </p>

        <p>
          We are located at 9 Parkway Close, Sheffield, S9 4WJ, or may be
          reached by email at
          <a href="mailto:hello@found.us">hello@found.us</a>. Please contact us
          with any issues with our Platform that you have so that we may attempt
          to resolve such issues.
        </p>
      </div>
    </HideableParagraph>

    <HideableParagraph title="Miscellaneous" text=" ">
      <div class="legal-text">
        <p>
          If any part of these Terms and Conditions shall be deemed unlawful,
          void or for any reason unenforceable, then that provision shall be
          deemed to be severable from these Terms and Conditions and shall not
          affect the validity and enforceability of any of the remaining
          provisions of the Terms and Conditions.
        </p>

        <p>
          These Terms and Conditions and our Privacy Policies, and any other
          terms or agreements that may be posted on the Platform (as may be
          amended from time to time) (“Platform Agreements”) contain the entire
          agreement between you and us relating to the Platform and your use of
          the Platform and supersede any previous agreements, arrangements,
          undertakings or proposals, written or oral, between you and us in
          relation to such matters. No oral explanation or oral information
          shall alter the interpretation of these Platform Agreements. You
          confirm that, in agreeing to accept these Platform Agreements, you
          have not relied on any representation except insofar as the same has
          expressly been made a representation in these Platform Agreements, and
          you agree that you shall have no remedy in respect of any
          representation which has not become a term of these Platform
          Agreements.
        </p>

        <p>
          You may send us notices or communicate with us by email at
          <a href="mailto:hello@found.us">hello@found.us</a>. If you send us an
          email that asks for a response, and you do not receive a response
          within ten (10) business days, please send us another email as we may
          not have received your previous email. When you send e-mail to us, you
          are communicating with us electronically, and you agree that we may
          communicate with you electronically. You acknowledge that that
          communications by email are not considered confidential
          communications. Therefore, please do not send us any confidential
          information by email.
        </p>
      </div>
    </HideableParagraph>
  </div>
</template>

<script>
import HideableParagraph from '@/components/HideableParagraph';

export default {
  name: 'FundingTermsUS',
  components: {
    HideableParagraph
  }
};
</script>

<style lang="scss" scoped>
.funding-terms {
  * {
    text-align: justify;
  }
}

a {
  color: inherit;
  text-decoration: underline;
}
</style>
